import { useCallback, useEffect, useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from '../components/Toast';
import { DataTable } from '../components/DataTable';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { useFilasConIdentidad } from '../lib/identidadArticulos';

/**
 * "Publicados sin composición" (2026-09-29; desde 2026-10-20 en Sincronizar con ML, antes en Sincronización por SKU):
 * SKU con publicaciones vinculadas sin BOM y que no son artículos de un proveedor, así que el ERP no puede calcular su
 * stock. Sólo lectura (GET ml/sync-sku/sin-composicion). Carga al montarse: va dentro de un panel que se abre a demanda.
 */

interface FilaSinComposicion {
  sku: string;
  descripcion: string | null;
  publicaciones: number;
  muestraMla: string[];
  pareceUnPack: boolean;
}

interface ReporteSinComposicion {
  total: number;
  filas: FilaSinComposicion[];
}

const n = (v: number) => v.toLocaleString('es-AR');

export function PublicadosSinComposicion() {
  const { addToast } = useToast();
  const [reporte, setReporte] = useState<ReporteSinComposicion | null>(null);
  const filas = useFilasConIdentidad(reporte?.filas ?? [], (f) => f.sku);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setReporte(await api.get<ReporteSinComposicion>('/api/v3/ml/sync-sku/sin-composicion'));
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setCargando(false);
    }
  }, [addToast]);
  useEffect(() => { void cargar(); }, [cargar]);

  return (
    <section aria-label="Publicados sin composición" className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-800">Publicados sin composición</h3>
          {reporte && <Badge tone={reporte.total > 0 ? 'amber' : 'green'}>{`${n(reporte.total)} SKU`}</Badge>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => void cargar()} loading={cargando}>Actualizar</Button>
      </div>
      <p className="text-sm text-slate-600 max-w-4xl">
        SKU con publicaciones vinculadas que no tienen composición y no son artículos de un proveedor, así que el ERP no puede
        calcular su stock ni sincronizarlo. El ERP no sabe de qué están hechos: completá sus componentes y cantidades en la planilla
        02_CODIGOS y se corrigen la próxima vez que se traigan las publicaciones (cada 15 min).
      </p>
      {reporte && reporte.total > reporte.filas.length && (
        <p className="text-xs text-slate-500">{`Mostrando ${n(reporte.filas.length)} de ${n(reporte.total)} (los que tienen más publicaciones primero).`}</p>
      )}
      <DataTable<FilaSinComposicion>
        title="Publicados-sin-composicion"
        enableXlsxExport={false}
        data={filas}
        loading={cargando && !reporte}
        emptyMessage="Todos los SKU publicados tienen composición o son artículos de proveedor"
        columns={[
          { key: 'codigoArticulo', header: 'SKU', render: (f) => <CodigoArticulo sku={f.sku} /> },
          { key: 'proveedorArticulo', header: 'Proveedor' },
          { key: 'descripcion', header: 'Descripción', render: (f) => f.descripcion ?? '—' },
          { key: 'publicaciones', header: 'Publicaciones', align: 'right', render: (f) => n(f.publicaciones) },
          {
            key: 'muestraMla', header: 'Ejemplos de publicaciones', sortable: false, filterable: false,
            render: (f) => <span className="text-xs">{f.muestraMla.join(', ')}{f.publicaciones > f.muestraMla.length ? '…' : ''}</span>,
          },
          {
            key: 'pareceUnPack', header: 'Pista', sortable: false, filterable: false,
            render: (f) => (f.pareceUnPack ? <Badge tone="blue">Parece un pack (por el nombre)</Badge> : null),
          },
        ]}
      />
    </section>
  );
}
